/**
 * CALGARY-NE — roster wave entries. GENERATED BY ASSEMBLY, WRITTEN BY REVIEW.
 *
 * Geography (streets, landmarks, coordinates, adjacency) ported from the source
 * roster; airspace derived by the calibrated classifier in
 * scripts/derive-airspace.mjs; every descriptive sentence written fresh from
 * aerial-media truth and fact-checked against its generation brief. See
 * docs/geo/VISION-v3.md for the fact contract these entries were written under.
 *
 * 24 entries. Edit by hand freely — assembly never overwrites a
 * file that has drifted from its fragments without --force.
 */
import type { Location } from "../types";

export const ROSTER_CALGARY_NE: Location[] = [
// ───────────────────────────────────────────────────────────── ABBEYDALE ──
  {
    slug: "abbeydale",
    name: "Abbeydale",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0708, lng: -113.9505 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 7,
      authorizationRequired: true,
      authorizationNote:
        "Abbeydale lies inside the charted CYYC control zone, about 7 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Flight plans here also keep clear of Peter Lougheed Centre heliport, operating about 3 km away. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Abbeydale's streets loop and branch off Abbeydale Drive SE in the tight curvilinear pattern of its era, and from above the community reads as one continuous field of similar rooflines.",
      consequence:
        "That uniformity works against a single listing. A ground-level photo looks like every second house on the block, and a careless aerial wide dissolves the property into the pattern instead of selling it.",
      resolution:
        "A low oblique orbit holds the subject roofline at the centre of the frame and uses a hard edge — 17 Avenue SE or the open green of Elliston Park — to give the eye a place to land, delivered in the standard turnaround.",
    },
    landmarks: ["Abbeydale Drive SE", "17 Avenue SE", "Elliston Park", "Marlborough Mall"],
    terrain:
      "Level prairie grade on Calgary's east side. The lot pattern is compact and regular, and from altitude the community reads as an even field of rooflines with Elliston Park as the nearest open break.",
    windNote:
      "Chinook belt, with no relief to break it. A west wind that has crossed the whole city arrives here at full strength, and the gust spread matters more than the average on the forecast.",
    seasonalWindow:
      "Flat and open, so golden hour runs close to its full published length at both ends of the day. Chinook thaws strip snow quickly in winter; green-up around Elliston Park reads from the air weeks after the melt.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and pathways, so launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes east of the Calgary base. An Abbeydale shoot pairs naturally with Marlborough or Penbrooke Meadows on the same trip.",
    faqs: [
      {
        question: "Does an Abbeydale drone shoot need NAV CANADA approval?",
        answer:
          "Yes. Abbeydale sits about 7 km from Calgary International, inside the CYYC Class C control zone, so flights require an Advanced RPAS certificate and a NAV CANADA authorisation. Fly4MEdia files that authorisation as part of booking and confirms the site and altitude against NAV Drone before every flight.",
      },
      {
        question: "How do you make one Abbeydale house stand out from the air?",
        answer:
          "Composition. The community's rooflines are similar in pitch and colour, so the frame is built to isolate the subject: a low orbit keeps the property centred while 17 Avenue SE or Elliston Park anchors the edge of the shot. The uniformity becomes context instead of camouflage.",
      },
    ],
    primaryKeywords: [
      "drone photography Abbeydale",
      "aerial photography Abbeydale",
      "Abbeydale drone services",
    ],
    nearestLocations: ["marlborough", "marlborough-park", "penbrooke-meadows", "rundle"],
  },

  // ──────────────────────────────────────────────────────── APPLEWOOD PARK ──
  {
    slug: "applewood-park",
    name: "Applewood Park",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0619, lng: -113.9482 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 8,
      authorizationRequired: true,
      authorizationNote:
        "Applewood Park sits about 8 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Peter Lougheed Centre heliport operates about 3 km away, and flight plans keep clear of its approaches. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Applewood Park sits on Calgary's flat east side, and a chinook that has crossed the entire city arrives at 68 Street SE with nothing left in its path to slow it down.",
      consequence:
        "On a gusty afternoon that shows up as shake in stabilised video and drift in long exposures. A shoot booked against the citywide average wind speed, rather than the gust spread, is the shoot that gets rescheduled.",
      resolution:
        "Flight windows here are set against forecast gusts, not averages — usually the calm early hours or the evening drop — and the footage is delivered in the standard turnaround.",
    },
    landmarks: ["Applewood Park", "Elliston Park", "Marlborough Mall", "68 Street SE"],
    terrain:
      "Flat prairie grade on the city's east side, with compact residential streets opening onto the green of Applewood Park and the open ground of Elliston Park close by.",
    windNote:
      "Squarely in the chinook belt and fully exposed. There is no valley or ridge here to shelter a launch site; wind at street level closely tracks wind at altitude.",
    seasonalWindow:
      "Open, level ground means the low sun works the frame at both ends of the day. Snow cover comes and goes with each chinook through winter, and green-up in Applewood Park marks the start of the reliable spring window.",
    bylawNote:
      "Calgary's parks bylaws do not permit drone launch or recovery in municipal parks or on pathways; takeoff is staged from private property with the client's permission.",
    subjectMix: ["luxury-residential", "commercial-retail", "construction-progress"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. Applewood Park pairs easily with Abbeydale or Marlborough Park on a single trip east.",
    faqs: [
      {
        question: "Is drone photography allowed in Applewood Park given the airport?",
        answer:
          "Yes, with authorisation. Applewood Park sits about 8 km from Calgary International inside the CYYC Class C control zone, so every flight needs an Advanced RPAS certificate and a NAV CANADA authorisation. That paperwork is filed as part of booking, and the site is checked against NAV Drone before the flight.",
      },
      {
        question: "Does wind cancel many shoots on Calgary's east side?",
        answer:
          "Fewer than you would expect, because the windows are chosen carefully. The east side is exposed, so bookings target the calm ends of the day and are set against forecast gust spread rather than average speed. When a chinook makes the afternoon unusable, the shoot moves, not the standard.",
      },
    ],
    primaryKeywords: [
      "drone photography Applewood Park",
      "aerial photography Applewood Park",
      "Applewood Park drone services",
    ],
    nearestLocations: ["marlborough-park", "abbeydale", "penbrooke-meadows"],
  },

  // ────────────────────────────────────────────────── BRIDGELAND/RIVERSIDE ──
  {
    slug: "bridgeland-riverside",
    name: "Bridgeland/Riverside",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0533, lng: -114.0389 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 8,
      authorizationRequired: true,
      authorizationNote:
        "Bridgeland/Riverside sits about 8 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Peter Lougheed Centre heliport operates about 5 km away, and flight plans keep clear of its approaches. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Tom Campbell's Hill Park stands directly over Bridgeland's streets, and on a chinook day the west wind spills over that slope and arrives below as gusty, tumbling air that no citywide forecast records.",
      consequence:
        "The turbulence lives on the lee side of the hill — exactly where the neighbourhood's best low frames are composed. An orbit planned from a desk can be smooth on paper and rough in the air.",
      resolution:
        "Launch position and orbit direction are set around the hill on the day, or the shoot holds for the evening lull when the gusts settle — with delivery in the standard turnaround.",
    },
    landmarks: [
      "Memorial Drive NE",
      "Tom Campbell's Hill Park",
      "Murdoch Park",
      "Bridgeland/Memorial LRT Station",
    ],
    terrain:
      "An inner-city slope: the grade falls from Tom Campbell's Hill Park down through the streets toward Memorial Drive NE, so the community reads in stepped layers from the air.",
    windNote:
      "Chinook belt, complicated by relief. West wind accelerates over the slope of Tom Campbell's Hill Park and produces mechanical turbulence on its lee side; gusts below the hill outrun the citywide numbers.",
    seasonalWindow:
      "The fall of the land toward Memorial Drive NE takes low evening sun across its face, separating rooflines that midday light flattens. Snow clears the sunny grades early and holds in the shade below the hill.",
    bylawNote:
      "Municipal parks and pathways in Calgary are off limits for drone takeoff and landing under the city's parks bylaws, so flights are launched from private property with the owner's consent.",
    subjectMix: [
      "luxury-residential",
      "new-build-subdivision",
      "commercial-retail",
      "construction-progress",
    ],
    mobilisationNote:
      "About 15 minutes from base — close enough to fly on short notice when a calm window opens. Pairs well with Renfrew or Crescent Heights on the same outing.",
    faqs: [
      {
        question: "What does the CYYC control zone mean for a drone shoot in Bridgeland/Riverside?",
        answer:
          "It means the flight is authorised before it is booked. Bridgeland/Riverside sits about 8 km from Calgary International inside the Class C control zone, so an Advanced RPAS certificate and NAV CANADA authorisation are required. Fly4MEdia files the authorisation during booking and verifies the exact site against NAV Drone before flying.",
      },
      {
        question: "Why shoot Bridgeland from the air rather than from Tom Campbell's Hill?",
        answer:
          "The hill gives one fixed angle; the drone gives every angle. A hilltop photo also cannot place the subject property in its own foreground. A low aerial orbit keeps the listing large in frame with the slope, the rooftops and Memorial Drive NE behind it — a composition no ground vantage in the neighbourhood can produce.",
      },
      {
        question: "When is the best light for a Bridgeland/Riverside shoot?",
        answer:
          "Evening, twice over. Low sun rakes across the slope below Tom Campbell's Hill Park and separates the layers of the neighbourhood, and the wind coming over the hill usually settles toward dusk. That pairing of light and calm is the window Bridgeland bookings aim for.",
      },
    ],
    primaryKeywords: [
      "drone photography Bridgeland/Riverside",
      "aerial photography Bridgeland/Riverside",
      "Bridgeland/Riverside drone services",
    ],
    nearestLocations: ["renfrew", "crescent-heights", "winston-heights-mountview"],
  },

  // ─────────────────────────────────────────────────────────── CASTLERIDGE ──
  {
    slug: "castleridge",
    name: "Castleridge",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1109, lng: -113.977 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 3,
      authorizationRequired: true,
      authorizationNote:
        "Castleridge lies inside the charted CYYC control zone, about 3 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Flight plans here also keep clear of Peter Lougheed Centre heliport, operating about 4 km away. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Castleridge sits on a level prairie grade, and level ground is generous with light: nothing blocks the low sun at either end of the day, so golden hour along Castleridge Boulevard NE runs close to its full published length.",
      consequence:
        "That is an asset most shoots here waste. At midday the same flatness gives the sun nothing to work with — no relief, no long shadow, and every roof reads the same shadowless grey.",
      resolution:
        "Bookings are held for the low-sun windows, when raking light puts depth back into the frame — an approach run down the boulevard or an orbit over Castleridge Park, delivered in the standard turnaround.",
    },
    landmarks: [
      "Castleridge Boulevard NE",
      "Castleridge Park",
      "Genesis Centre of Community Wellness",
      "Saddletowne CTrain Station",
    ],
    terrain:
      "Level prairie in Calgary's far northeast, laid out in looping residential streets with Castleridge Park and the grounds around the Genesis Centre of Community Wellness providing the open breaks in the pattern.",
    windNote:
      "Chinook belt and open to it. Flat terrain offers no shelter, so the west wind arrives at full strength; the practical difference between a flyable and an unflyable day here is the spread between average and gust.",
    seasonalWindow:
      "Flat terrain keeps terrain shadow out of the equation — first and last light are usable at their full length. Chinooks cycle the snow cover all winter, so a clean white frame is a matter of timing, not season.",
    bylawNote:
      "Drone takeoff and landing in City of Calgary parks and on public pathways is restricted by the parks bylaws; launches are arranged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from base. Castleridge sits beside Martindale and Taradale, so two or three northeast bookings often share one trip.",
    faqs: [
      {
        question: "Castleridge is only 3 km from the airport — can a drone fly there at all?",
        answer:
          "Yes, legally and routinely, but only under authorisation. Castleridge sits inside the CYYC Class C control zone, so flights require an Advanced RPAS certificate and a NAV CANADA authorisation, and altitudes this close to the field can be conservative. The site and ceiling are confirmed against NAV Drone before every flight.",
      },
      {
        question: "What time of day photographs Castleridge best?",
        answer:
          "The first and last hour of sun. The community is flat, so midday light flattens it further; low sun is what gives rooflines shadow and the frame depth. Because no valley wall or high ground shades Castleridge, those golden-hour windows run effectively their full published length in every season.",
      },
    ],
    primaryKeywords: [
      "drone photography Castleridge",
      "aerial photography Castleridge",
      "Castleridge drone services",
    ],
    nearestLocations: ["saddle-ridge", "taradale", "martindale"],
  },

  // ───────────────────────────────────────────────────────── HIGHLAND PARK ──
  {
    slug: "highland-park",
    name: "Highland Park",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0714, lng: -114.0625 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 7,
      authorizationRequired: true,
      authorizationNote:
        "Highland Park sits about 7 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Foothills Medical Centre heliport operates about 5 km away, and flight plans keep clear of its approaches. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "From altitude, Highland Park is a grid interrupted by two green corridors — the fairways of Confederation Park Golf Course and the thin line of Nose Creek — and the creek is the only moving water in the frame.",
      consequence:
        "Those corridors are what buyers pay for proximity to, and ground photography cannot show the relationship. A listing two blocks from the golf course looks, from the sidewalk, like a listing anywhere.",
      resolution:
        "A boundary-line orbit composes the property against the green — fairways or creek corridor filling the background — so the location does the selling, delivered in the standard turnaround.",
    },
    landmarks: [
      "Nose Creek",
      "Confederation Park Golf Course",
      "James Fowler High School",
      "Northmount Drive NW",
    ],
    terrain:
      "A gentle north-central grade falling toward the Nose Creek corridor, with the fairways of Confederation Park Golf Course cutting the residential grid on the community's flank.",
    windNote:
      "Chinook belt. The Nose Creek corridor can channel a light drainage flow on calm mornings, but the dominant pattern is the west wind, arriving here with little to break it.",
    seasonalWindow:
      "Green is the community's signature from the air, so the reliable window opens with spring green-up on the fairways and holds until the leaves turn. Winter frames trade green for the clean line of the creek in snow.",
    bylawNote:
      "City parks bylaws in Calgary restrict drone takeoff and landing in municipal parks and along pathways, so launches stage from private property with the owner's permission.",
    subjectMix: ["luxury-residential", "new-build-subdivision", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the Calgary base, close enough for short-notice light-chasing. Pairs naturally with Thorncliffe or Cambrian Heights.",
    faqs: [
      {
        question: "Do Highland Park drone flights need clearance from NAV CANADA?",
        answer:
          "Yes. Highland Park is about 7 km from Calgary International, inside the CYYC Class C control zone, so an Advanced RPAS certificate and NAV CANADA authorisation are required for every flight. Fly4MEdia also plans clear of the Foothills Medical Centre heliport approaches and confirms the site in NAV Drone first.",
      },
      {
        question: "Can you photograph a Highland Park home with the golf course in frame?",
        answer:
          "Yes — that is usually the point of flying here. An orbit is positioned so the fairways of Confederation Park Golf Course or the Nose Creek corridor sit behind the property, showing the proximity a caption can only claim. The flight itself launches from private property, not from the course.",
      },
    ],
    primaryKeywords: [
      "drone photography Highland Park",
      "aerial photography Highland Park",
      "Highland Park drone services",
    ],
    nearestLocations: ["cambrian-heights", "thorncliffe", "winston-heights-mountview"],
  },

  // ───────────────────────────────────────────────────────────── HOMESTEAD ──
  {
    slug: "homestead",
    name: "Homestead",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1218, lng: -113.9508 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 4,
      authorizationRequired: true,
      authorizationNote:
        "Homestead lies inside the charted CYYC control zone, about 4 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Peter Lougheed Centre heliport operates about 5 km away, and flight plans keep clear of its approaches. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Homestead is young enough that its tree canopy is still years from doing any work, so wind that crosses Stoney Trail NE off the open prairie reaches street level unbroken — there is no shelter layer here yet.",
      consequence:
        "Ground gusts match altitude gusts, and on a construction site that also means dust across the lens exactly when the light is best. Flight windows here run shorter than the citywide forecast implies.",
      resolution:
        "Shoots are timed to the calm ends of the day and the gust spread is checked on site before launch — a builder's progress series holds its schedule that way, delivered in the standard turnaround.",
    },
    landmarks: ["Homestead Boulevard NE", "Homestead Park", "Stoney Trail NE", "Metis Trail NE"],
    terrain:
      "Flat, newly graded prairie at the city's northeast edge, its street pattern still filling in around Homestead Boulevard NE and open ground running out beyond Stoney Trail NE.",
    windNote:
      "Chinook belt with maximum exposure: no mature trees, no relief, open fetch from the west and north. Surface wind here is the closest thing in the city to the raw forecast number.",
    seasonalWindow:
      "New landscaping greens late and browns early, so summer frames read greener each year as the community matures. Winter chinooks strip the snow fast on this open grade; a white frame needs same-week timing.",
    bylawNote:
      "Takeoff and landing inside City of Calgary parks — Homestead Park included — is restricted by the parks bylaws, so launches are staged from private land with the client's consent.",
    subjectMix: ["new-build-subdivision", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from base via Metis Trail NE or Stoney Trail NE. Progress-series visits pair with Redstone, Skyview Ranch or Saddle Ridge in one northeast run.",
    faqs: [
      {
        question: "Homestead is close to the airport — does that stop construction progress flights?",
        answer:
          "No, it structures them. Homestead sits about 4 km from Calgary International inside the CYYC Class C control zone, so each flight operates under an Advanced RPAS certificate with NAV CANADA authorisation, filed as part of booking. Recurring progress flights reuse the same planning, which keeps a monthly series predictable.",
      },
      {
        question: "Why book a fixed monthly progress series in Homestead rather than ad-hoc flights?",
        answer:
          "Because the comparison is the product. A progress series flown from the same positions and altitudes each visit turns raw footage into a measurable record — framing, lot lines and stage of build line up month over month. Ad-hoc flights produce pictures; a series produces evidence of schedule.",
      },
    ],
    primaryKeywords: [
      "drone photography Homestead",
      "aerial photography Homestead",
      "Homestead drone services",
    ],
    nearestLocations: ["saddle-ridge", "redstone", "skyview-ranch"],
  },

  // ─────────────────────────────────────────────────────────── MARLBOROUGH ──
  {
    slug: "marlborough",
    name: "Marlborough",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0619, lng: -113.9903 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 7,
      authorizationRequired: true,
      authorizationNote:
        "Marlborough sits about 7 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Peter Lougheed Centre heliport operates about 2 km away, and flight plans keep clear of its approaches. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Marlborough sits inside a tight triangle of aviation: the CYYC control zone overhead, the airport itself 7 km away and the Peter Lougheed Centre heliport only 2 km out — fixed-wing and rotor considerations overlapping the same sky.",
      consequence:
        "That geometry is why many drone quotes quietly exclude this part of the city. An operator with a Basic certificate cannot fly here at all, and one who ignores the heliport is planning a shoot that may not survive review.",
      resolution:
        "The authorisation is filed as part of booking, the flight plan keeps clear of the heliport approaches, and the site is confirmed in NAV Drone — the shoot date firms up once the paperwork is in hand.",
    },
    landmarks: ["Marlborough Mall", "Marlborough LRT Station", "36 Street NE", "Marlborough Way NE"],
    terrain:
      "Level east-side ground organised around the commercial block of Marlborough Mall and the LRT line, with residential streets fanning out from 36 Street NE.",
    windNote:
      "Chinook belt, flat and open. The west wind arrives across the whole city with no local relief to slow it, so the spread between steady wind and gusts decides whether an afternoon is flyable.",
    seasonalWindow:
      "Flat grade, full-length golden hours at both ends of the day. Winter snow cover cycles with every chinook, and the commercial roofscape reads cleanest in low raking light rather than at noon.",
    bylawNote:
      "Under Calgary's parks bylaws, municipal parks and pathways are not available for drone takeoff or landing, so launches operate from private property with the owner's permission.",
    subjectMix: ["commercial-retail", "luxury-residential", "municipal-infrastructure"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Marlborough anchors the inner east side, pairing readily with Rundle, Whitehorn or Mayland Heights in a single trip.",
    faqs: [
      {
        question: "Why is the airspace over Marlborough more complicated than most of Calgary?",
        answer:
          "Two facilities share it. Marlborough lies about 7 km from Calgary International inside the CYYC Class C control zone, and the Peter Lougheed Centre heliport operates roughly 2 km away. Flights need an Advanced RPAS certificate and NAV CANADA authorisation, with plans kept clear of the heliport approaches and confirmed in NAV Drone.",
      },
      {
        question: "Can you shoot commercial property around Marlborough Mall from the air?",
        answer:
          "Yes. Retail frontage, parking utilisation and access from the LRT are exactly what an aerial frame shows that a ground photo cannot. The flight is planned around the heliport approaches, launches from the client's own property, and the wide commercial context is delivered in the standard turnaround.",
      },
    ],
    primaryKeywords: [
      "drone photography Marlborough",
      "aerial photography Marlborough",
      "Marlborough drone services",
    ],
    nearestLocations: ["whitehorn", "penbrooke-meadows", "rundle", "mayland-heights"],
  },

  // ────────────────────────────────────────────────────── MARLBOROUGH PARK ──
  {
    slug: "marlborough-park",
    name: "Marlborough Park",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0642, lng: -113.9836 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 7,
      authorizationRequired: true,
      authorizationNote:
        "About 7 km from Calgary International, Marlborough Park falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Flight plans here also keep clear of Peter Lougheed Centre heliport, operating about 2 km away. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Between Memorial Drive NE and 16 Avenue NE, Marlborough Park lies almost perfectly level — there is no hill, ridge or escarpment anywhere in the community to photograph from.",
      consequence:
        "Which means every wide, orienting view of the neighbourhood that exists must be manufactured. A ground photographer here has no vantage to borrow; context shots simply are not available to them.",
      resolution:
        "A rising reveal supplies the vantage the terrain never built — lifting from the property to a height where Elliston Park, the LRT line and the arterial edges organise the frame — in the standard turnaround.",
    },
    landmarks: ["Elliston Park", "Franklin LRT Station", "Memorial Drive NE", "16 Avenue NE"],
    terrain:
      "Level prairie grade in Calgary's inner east, a residential grid framed by Memorial Drive NE and 16 Avenue NE with the open ground of Elliston Park breaking the pattern.",
    windNote:
      "Chinook belt. The flat grade neither shelters nor accelerates the west wind — conditions here track the citywide forecast closely, and the flyable hours are the ones where gusts stay near the average.",
    seasonalWindow:
      "With no relief to cast terrain shadow, golden hour runs its full length at both ends of the day. Chinook cycles strip and repaint the snow all winter; green-up around Elliston Park signals the spring window.",
    bylawNote:
      "Calgary's parks bylaws keep drone takeoff and landing out of municipal parks and off pathways, so every launch here is staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 15 minutes east of base. Sits between Marlborough, Rundle and Abbeydale, so multi-property days on the east side are simple to arrange.",
    faqs: [
      {
        question: "What approvals are needed before a drone flight in Marlborough Park?",
        answer:
          "An Advanced RPAS certificate and a NAV CANADA authorisation. The community sits about 7 km from Calgary International inside the CYYC Class C control zone, with the Peter Lougheed Centre heliport roughly 2 km away. Fly4MEdia files the authorisation at booking and confirms the exact site against NAV Drone before flying.",
      },
      {
        question: "Is there any high ground in Marlborough Park for elevated photos without a drone?",
        answer:
          "No — the community is level from edge to edge, which is precisely why aerial work earns its keep here. Any elevated view of a property, a street or the neighbourhood as a whole has to come from an aircraft. A short authorised flight replaces a vantage the terrain never provided.",
      },
    ],
    primaryKeywords: [
      "drone photography Marlborough Park",
      "aerial photography Marlborough Park",
      "Marlborough Park drone services",
    ],
    nearestLocations: ["marlborough", "rundle", "penbrooke-meadows", "abbeydale"],
  },

  // ──────────────────────────────────────────────────────────── MARTINDALE ──
  {
    slug: "martindale",
    name: "Martindale",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1214, lng: -113.9853 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 2,
      authorizationRequired: true,
      authorizationNote:
        "About 2 km from Calgary International, Martindale falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Flight plans here also keep clear of Peter Lougheed Centre heliport, operating about 5 km away. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Martindale's grade is prairie-flat, and this close to the airport that flatness is an asset: with no relief to block a sightline, even a conservative authorised altitude sees clear across the rooflines from Martindale Boulevard NE to Metis Trail NE.",
      consequence:
        "Operators sometimes assume a tight ceiling near CYYC kills wide coverage and decline the job. Here the assumption is wrong — but only a flight plan built around the granted altitude proves it.",
      resolution:
        "Low-altitude oblique work is planned to the ceiling the authorisation grants: orbits and approach runs composed to carry the whole community in frame, delivered in the standard turnaround.",
    },
    landmarks: [
      "Martindale Boulevard NE",
      "Martindale LRT Station",
      "Genesis Centre of Community Wellness",
      "Metis Trail NE",
    ],
    terrain:
      "Flat northeast prairie, a dense residential grid threaded by the LRT line and edged by the arterial traffic of Metis Trail NE, with community life centred on the Genesis Centre of Community Wellness.",
    windNote:
      "Chinook belt, fully exposed. Level terrain leaves nothing between the west wind and the streets, so flyable windows follow the gust forecast rather than the daily average.",
    seasonalWindow:
      "Level ground and open horizons give full-length golden hours in every season. Snow cover alternates with brown grass through the chinook months, so winter frames are booked to conditions, not dates.",
    bylawNote:
      "The City of Calgary's parks bylaws restrict drone takeoff and landing in municipal parks and along pathways; launches are staged from private property with the owner's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. Martindale sits shoulder to shoulder with Saddle Ridge, Taradale and Castleridge, so northeast bookings often share one trip.",
    faqs: [
      {
        question: "How close is Martindale to the CYYC flight paths, and does that limit shoots?",
        answer:
          "Martindale is about 2 km from Calgary International, inside the Class C control zone, so every flight requires an Advanced RPAS certificate and NAV CANADA authorisation, and granted altitudes can be conservative. It limits how high we fly, not whether we fly — the site and ceiling are confirmed in NAV Drone first.",
      },
      {
        question: "Can wide neighbourhood views still be shot at a lower altitude in Martindale?",
        answer:
          "Yes. The community is flat, so nothing interrupts a sightline — a modest altitude with an oblique camera angle carries from Martindale Boulevard NE across most of the grid. Composition replaces height: longer approach runs and wider orbits produce the establishing views a tighter ceiling seems to forbid.",
      },
    ],
    primaryKeywords: [
      "drone photography Martindale",
      "aerial photography Martindale",
      "Martindale drone services",
    ],
    nearestLocations: ["saddle-ridge", "taradale", "castleridge"],
  },

  // ─────────────────────────────────────────────────────── MAYLAND HEIGHTS ──
  {
    slug: "mayland-heights",
    name: "Mayland Heights",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0631, lng: -114.0086 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 7,
      authorizationRequired: true,
      authorizationNote:
        "Mayland Heights sits about 7 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Flight plans here also keep clear of Peter Lougheed Centre heliport, operating about 2 km away. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Mayland Heights earns its name: the community stands on a bluff above the Bow River pathway system, one of the few genuine drops in elevation anywhere in Calgary's east.",
      consequence:
        "From the street, that height is invisible. A listing along the escarpment edge is being sold short by any photo that cannot show what the property stands above.",
      resolution:
        "A rising reveal from below the brow of the hill restores the drop to the frame — the house first, then the edge, then the pathway corridor opening beneath it — delivered in the standard turnaround.",
    },
    landmarks: [
      "Mayland Heights Drive NE",
      "Bow River pathway system",
      "Mayland Heights Community Centre",
      "26 Avenue NE",
    ],
    terrain:
      "High ground falling away to the Bow River pathway system at the community's edge, with level residential streets on top around Mayland Heights Drive NE and 26 Avenue NE.",
    windNote:
      "Chinook belt with an edge effect: west wind climbs the escarpment and can be livelier at the brow than the forecast suggests, so launch sites are chosen back from the edge on gusty days.",
    seasonalWindow:
      "The west-facing edge takes the last light of the day — evening sun rakes up the slope and holds the escarpment in relief. Snow lingers on the shaded face after the chinooks have cleared the plateau above.",
    bylawNote:
      "City of Calgary parks bylaws restrict takeoff and landing in municipal parks and along the pathway network, so flights launch from private property with the owner's permission.",
    subjectMix: ["luxury-residential", "commercial-retail", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from base. The escarpment edge shoots best in the evening, and the short drive makes chasing that specific hour practical; pairs with Vista Heights or Bridgeland/Riverside.",
    faqs: [
      {
        question: "Is authorisation needed to fly a drone over Mayland Heights?",
        answer:
          "Yes. Mayland Heights sits about 7 km from Calgary International inside the CYYC Class C control zone, with the Peter Lougheed Centre heliport roughly 2 km away. Flights run under an Advanced RPAS certificate with NAV CANADA authorisation, planned clear of the heliport approaches and confirmed against NAV Drone beforehand.",
      },
      {
        question: "What does the escarpment add to a Mayland Heights listing video?",
        answer:
          "Everything the street view hides. The community's defining feature is the drop toward the Bow River pathway system, and only an aerial camera can hold the house and that drop in one frame. A rising reveal from below the edge shows buyers exactly what the elevation buys them.",
      },
    ],
    primaryKeywords: [
      "drone photography Mayland Heights",
      "aerial photography Mayland Heights",
      "Mayland Heights drone services",
    ],
    nearestLocations: ["vista-heights", "bridgeland-riverside", "penbrooke-meadows"],
  },

  // ───────────────────────────────────────────────────────── MONTEREY PARK ──
  {
    slug: "monterey-park",
    name: "Monterey Park",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0908, lng: -113.9433 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 6,
      authorizationRequired: true,
      authorizationNote:
        "Monterey Park sits about 6 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Peter Lougheed Centre heliport operates about 3 km away, and flight plans keep clear of its approaches. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "A Monterey Park winter is not one season but a cycle: each chinook strips the snow from the open grade around Maitland Green Park, and each cold snap paints it back, sometimes twice in the same week.",
      consequence:
        "So a winter shoot booked to a date is a coin flip — the frames may come back crisp white or patchy brown, and a listing shot in the brown week competes against one shot in the white.",
      resolution:
        "Winter bookings here are held to conditions rather than the calendar: the flight goes when the cover is right, a 20-minute mobilisation makes that practical, and delivery stays in the standard turnaround.",
    },
    landmarks: [
      "Monterey Drive NE",
      "Maitland Green Park",
      "Monterey Park Community Association",
      "52 Street NE",
    ],
    terrain:
      "Level to gently rolling prairie on Calgary's east side, residential streets curving off Monterey Drive NE with Maitland Green Park as the open ground at the community's core.",
    windNote:
      "Chinook belt, open and exposed. The west wind reaches the community across the full width of the city with no relief left to slow it; the gap between mean wind and gusts sets the flyable window.",
    seasonalWindow:
      "The chinook cycle rules winter, swinging ground cover from white to brown and back. Summer is steadier — long evening light across the open grade, with late-season wildfire smoke the main threat to contrast.",
    bylawNote:
      "Drone launches and recoveries in City of Calgary parks, Maitland Green Park included, are restricted under the parks bylaws; flights stage from private property with the client's consent.",
    subjectMix: ["luxury-residential", "new-build-subdivision", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. Sits beside Temple, Pineridge and Abbeydale, so east-side bookings group easily into one outing.",
    faqs: [
      {
        question: "What airspace rules apply to drone work in Monterey Park?",
        answer:
          "Monterey Park lies about 6 km from Calgary International, inside the CYYC Class C control zone, so flights require an Advanced RPAS certificate and a NAV CANADA authorisation obtained before the shoot is confirmed. Plans also keep clear of the Peter Lougheed Centre heliport approaches, and the site is verified in NAV Drone.",
      },
      {
        question: "When should a winter listing in Monterey Park be photographed?",
        answer:
          "In the right week, not on a fixed date. Chinooks cycle the snow cover here repeatedly through winter, so the booking is held flexible and flown when the open ground around Maitland Green Park is cleanly white or fully bare. Twenty minutes from base, that flexibility costs nothing and decides how the listing reads.",
      },
    ],
    primaryKeywords: [
      "drone photography Monterey Park",
      "aerial photography Monterey Park",
      "Monterey Park drone services",
    ],
    nearestLocations: ["temple", "pineridge", "abbeydale"],
  },

  // ───────────────────────────────────────────────────── PENBROOKE MEADOWS ──
  {
    slug: "penbrooke-meadows",
    name: "Penbrooke Meadows",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0322, lng: -113.9903 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 10,
      authorizationRequired: true,
      authorizationNote:
        "Penbrooke Meadows sits about 10 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Peter Lougheed Centre heliport operates about 5 km away, and flight plans keep clear of its approaches. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The widest Penbrooke Meadows frames almost always carry Elliston Park's open ground in them, and that ground wears the season honestly — snow-scoured in deep winter, chinook-brown through the thaws, and fully green weeks after the calendar says spring.",
      consequence:
        "A shoot booked blind inherits whatever the park is wearing that week. The same address can read bleak in a brown frame and inviting in a green one, and the client only finds out at delivery.",
      resolution:
        "The booking is timed to the ground, not the month — flown when the green is in or the snow is clean — which a 15-minute mobilisation from base makes easy, with delivery in the standard turnaround.",
    },
    landmarks: [
      "Penbrooke Drive SE",
      "17 Avenue SE",
      "Elliston Park",
      "Penbrooke Meadows Community Association",
    ],
    terrain:
      "Level east-side prairie along the 17 Avenue SE corridor, a compact residential grid with the open expanse of Elliston Park dominating the wide view from altitude.",
    windNote:
      "Chinook belt, level and unsheltered. The west wind crosses the city and arrives here intact, so shoot windows are chosen where the gusts, not just the averages, sit inside the airframe's limits.",
    seasonalWindow:
      "Spring green-up transforms the wide frame more here than in most communities, because so much open ground sits in it. Summer evenings give long raking light down the avenue corridor; chinooks churn the winter cover.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks such as Elliston Park and along pathways, so launches stage from private property with the owner's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 15 minutes from the Calgary base along 17 Avenue SE. Pairs naturally with Marlborough, Dover or Forest Lawn for a multi-stop east-side day.",
    faqs: [
      {
        question: "Do you need special permission to fly a drone in Penbrooke Meadows?",
        answer:
          "Yes. Although Penbrooke Meadows is about 10 km from Calgary International — farther than most of the northeast — it still sits inside the CYYC Class C control zone. Flights need an Advanced RPAS certificate and NAV CANADA authorisation, filed at booking, with the site confirmed against NAV Drone before the flight.",
      },
      {
        question: "What season shows Penbrooke Meadows best from the air?",
        answer:
          "Early summer, once green-up reaches Elliston Park. So much open ground sits in the community's wide frames that the park's condition sets the tone of every establishing shot. Winter works when snow is fresh and complete; the brown chinook weeks in between are the ones worth avoiding.",
      },
    ],
    primaryKeywords: [
      "drone photography Penbrooke Meadows",
      "aerial photography Penbrooke Meadows",
      "Penbrooke Meadows drone services",
    ],
    nearestLocations: ["marlborough", "dover", "forest-lawn"],
  },
{
    slug: "pineridge",
    name: "Pineridge",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0917, lng: -113.9722 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 4,
      authorizationRequired: true,
      authorizationNote:
        "About 4 km from Calgary International, Pineridge falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Flight plans here also keep clear of Peter Lougheed Centre heliport, operating about 2 km away. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Pineridge has no terrain to its west — the prairie runs level all the way across the city, so the last hour of sun arrives low and unbroken, raking across the rooflines along Pinecliff Drive NE instead of being cut off early.",
      consequence:
        "Shot at mid-day, that flat ground makes every frame look the same — no shadow, no depth, no reason for one listing to stand apart. The light that separates Pineridge houses exists for one short window each evening, and most ground photographers are gone before it opens.",
      resolution:
        "A golden-hour hold is scheduled against the actual sunset rather than the calendar — the aircraft is up when the low light is doing its work, and the finished set is delivered in the standard turnaround.",
    },
    landmarks: [
      "Pinelands Park",
      "Pinecliff Drive NE",
      "36 Street NE",
      "Peigan Trail NE",
      "Pineridge Community Association",
    ],
    terrain:
      "Flat east-side prairie on the city grid. The ground barely moves across the whole community, so what varies from the air is canopy and roofline pattern, not the land.",
    windNote:
      "Chinook belt with no relief to break the west wind before it arrives. Gust spread decides flyability here, and it can widen quickly on a chinook day.",
    seasonalWindow:
      "The flat western horizon gives Pineridge a long, late golden hour in summer. Chinooks strip the snow cover repeatedly through winter, and late-summer wildfire smoke is the main threat to contrast.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and pathways, Pinelands Park included, so launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. Same-day scheduling is realistic, and a Pineridge visit pairs naturally with Temple or Rundle next door.",
    faqs: [
      {
        question: "Do you need special permission to fly a drone in Pineridge?",
        answer:
          "Yes. Pineridge sits about 4 km from Calgary International, inside the CYYC Class C control zone, so flights need an Advanced RPAS certificate and a NAV CANADA authorisation. Fly4MEdia holds the certification and files the authorisation before your shoot date is confirmed — it is part of booking, not an extra.",
      },
      {
        question: "When is the best light for aerial photos in Pineridge?",
        answer:
          "The last hour before sunset. Pineridge is flat, so mid-day sun leaves frames without shadow or depth. With no terrain to the west, low evening light crosses the community unbroken and rakes across the rooflines — the one window where an ordinary street reads as dimensional from the air.",
      },
      {
        question: "Does the hospital heliport nearby affect a Pineridge shoot?",
        answer:
          "It shapes the flight plan rather than blocking it. The Peter Lougheed Centre heliport operates about 2 km away, so routes over Pineridge are planned to stay clear of its approaches. That planning happens before the booking is confirmed, alongside the NAV CANADA authorisation for the CYYC control zone.",
      },
    ],
    primaryKeywords: [
      "drone photography Pineridge",
      "aerial photography Pineridge",
      "Pineridge drone services",
    ],
    nearestLocations: ["temple", "rundle", "monterey-park", "erin-woods"],
  },

  {
    slug: "redstone",
    name: "Redstone",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1489, lng: -113.9622 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 5,
      authorizationRequired: true,
      authorizationNote:
        "Redstone sits about 5 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The centre of Redstone is water the community built for itself — Redstone Pond is engineered stormwater, but from the air it reads as a genuine lake with a full ring of shoreline path.",
      consequence:
        "From the street the pond is a fence line and a screen of reeds. A listing two streets away cannot prove the water exists, which surrenders the one feature separating Redstone from every other new community along Stoney Trail NE.",
      resolution:
        "A boundary-line orbit puts the pond, the property and the community's edge into one continuous frame, delivered in the standard turnaround.",
    },
    landmarks: [
      "Redstone Pond",
      "Redstone Boulevard NE",
      "Stoney Trail NE",
      "Redstone Village retail",
      "88 Avenue NE",
    ],
    terrain:
      "New-build prairie at the city's north edge — level ground organised into crescents around a central pond, with open farmland still visible beyond the last streets.",
    windNote:
      "Open chinook country with farmland to the north and no shelter in any direction. West wind arrives at full strength, and gust spread is the number that decides whether the aircraft flies.",
    seasonalWindow:
      "Construction and green-up drive the calendar here as much as light does — spring and summer show the young streetscapes at their best, while winter chinooks repeatedly strip the snow. Evening light runs long and low across the level ground.",
    bylawNote:
      "City of Calgary bylaws restrict drone takeoff and landing in municipal parks and on pathways, so Redstone launches are staged from private property with the client's consent.",
    subjectMix: ["new-build-subdivision", "construction-progress", "luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the Calgary base via Stoney Trail NE. Easy to pair with Skyview Ranch, Savanna or Saddle Ridge in a single trip.",
    faqs: [
      {
        question: "Is drone photography allowed this close to Calgary International?",
        answer:
          "Yes, with the right credentials. Redstone sits about 5 km from the airport inside the CYYC Class C control zone, so every flight requires an Advanced RPAS certificate and a NAV CANADA authorisation. Fly4MEdia files that authorisation as part of booking and confirms the site against NAV Drone before flying.",
      },
      {
        question: "Can you show Redstone Pond in a property shoot?",
        answer:
          "Yes — it is usually the frame worth leading with. The pond is invisible from most streets, but an aerial orbit places a home in relation to the water and the pathway ring around it. For listings near the pond, that single frame explains the location faster than any description.",
      },
      {
        question: "Do you photograph construction progress in Redstone?",
        answer:
          "Yes. Redstone is still building out, and repeat aerial passes flown at a consistent altitude and heading give builders a clean progress series. Because the airspace authorisation is handled per booking, a recurring monthly flight is a scheduling matter rather than new paperwork each time.",
      },
    ],
    primaryKeywords: [
      "drone photography Redstone",
      "aerial photography Redstone",
      "Redstone drone services",
    ],
    nearestLocations: ["skyview-ranch", "saddle-ridge", "savanna"],
  },

  {
    slug: "renfrew",
    name: "Renfrew",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0578, lng: -114.0367 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 7,
      authorizationRequired: true,
      authorizationNote:
        "About 7 km from Calgary International, Renfrew falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Flight plans here also keep clear of Peter Lougheed Centre heliport, operating about 4 km away. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Renfrew sits under two overlapping pieces of working sky — about 7 km from Calgary International inside the CYYC control zone, with the Peter Lougheed Centre heliport operating about 4 km away — some of the most closely supervised low-level airspace in the city.",
      consequence:
        "Operators holding only a Basic RPAS certificate cannot fly here at all, and many who could simply decline inner-northeast addresses rather than manage the approvals. The result is Renfrew listings going to market without the aerial frames their pricing deserves.",
      resolution:
        "Advanced certification and the NAV CANADA authorisation are handled as part of booking, so a Renfrew shoot proceeds like any other — planned, flown and delivered in the standard turnaround.",
    },
    landmarks: [
      "Renfrew Athletic Park",
      "Bow River Pathway",
      "Edmonton Trail NE",
      "16 Avenue NE",
      "Regal Crescent NE",
    ],
    terrain:
      "Inner-city grid on ground that eases south toward the Bow River Pathway — mature streets and established canopy across one of the older quarters of the northeast.",
    windNote:
      "Chinook belt like the rest of the city, and the nearby Bow valley can add local stir along its rim. The working rule is unchanged — watch the gust spread, not the average.",
    seasonalWindow:
      "Mature canopy means summer frames read green and full, while winter opens the sightlines across the grid. Evening light runs late in high summer and finishes by late afternoon in the depths of winter.",
    bylawNote:
      "Drone takeoff and landing in City of Calgary parks — Renfrew Athletic Park included — is restricted by parks bylaws, so launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Close enough that a Renfrew shoot slots into a morning or evening window, and pairs easily with Bridgeland/Riverside or Winston Heights/Mountview.",
    faqs: [
      {
        question: "Can a drone legally be flown over Renfrew?",
        answer:
          "Yes, under the right authority. Renfrew is inside the CYYC Class C control zone, about 7 km from Calgary International, with the Peter Lougheed Centre heliport about 4 km away. Flights require an Advanced RPAS certificate and a NAV CANADA authorisation, which Fly4MEdia files before confirming the date.",
      },
      {
        question: "Can you shoot along the Bow River Pathway side of Renfrew?",
        answer:
          "Yes. The pathway corridor gives Renfrew listings a strong natural edge to compose against, and flights over it are planned within the site's authorised altitude. Because City of Calgary bylaws restrict park launches, the aircraft lifts off from private property nearby with the owner's consent.",
      },
      {
        question: "Why do some operators refuse inner-northeast Calgary addresses?",
        answer:
          "Because the approvals are real work. This part of the city carries the CYYC control zone plus hospital heliport traffic, and a Basic RPAS certificate does not permit flight here. Operators without Advanced certification exclude the area; Fly4MEdia treats the authorisation as a routine part of booking.",
      },
    ],
    primaryKeywords: [
      "drone photography Renfrew",
      "aerial photography Renfrew",
      "Renfrew drone services",
    ],
    nearestLocations: ["bridgeland-riverside", "crescent-heights", "winston-heights-mountview", "mayland-heights"],
  },

  {
    slug: "rundle",
    name: "Rundle",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0722, lng: -113.9819 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 6,
      authorizationRequired: true,
      authorizationNote:
        "About 6 km from Calgary International, Rundle falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Peter Lougheed Centre heliport operates about 1 km away, and flight plans keep clear of its approaches. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The open fields at Rundle Park are an honest wind gauge — flat, exposed and without a windbreak to the west, they show a chinook gust the moment it arrives across the city.",
      consequence:
        "Wind is the silent variable on this side of Calgary. A shoot booked to a calendar date rather than a gust forecast stands a real chance of meeting conditions the aircraft cannot safely fly, and the appointment is burned.",
      resolution:
        "Flight windows are booked against the forecast gust spread, and because the NAV CANADA authorisation is filed as part of booking, moving a date for wind is a calendar change rather than fresh paperwork — the set still lands in the standard turnaround.",
    },
    landmarks: [
      "Rundle Park",
      "Rundle LRT Station",
      "Rundlehorn Drive NE",
      "36 Street NE",
    ],
    terrain:
      "Flat northeast grid with generous green space — the ground is level enough that the community's aerial structure comes from streets and rooflines rather than relief.",
    windNote:
      "Full chinook exposure on level prairie. Nothing upwind slows a west gust down, and the spread between average and gust decides flyability more than the raw speed.",
    seasonalWindow:
      "Chinooks strip and repaint the snow cover all winter, so white-ground frames are a matter of timing. Summer evenings bring long low light across the flat grid; late-summer smoke is the main contrast risk.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks such as Rundle Park, so launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Comfortably a same-day booking, and easily paired with Whitehorn, Marlborough or Temple in one trip.",
    faqs: [
      {
        question: "What does the airport's control zone mean for a Rundle shoot?",
        answer:
          "It means credentials, not cancellation. Rundle sits about 6 km from Calgary International inside the CYYC Class C control zone, and the Peter Lougheed Centre heliport operates about 1 km away. Fly4MEdia flies under an Advanced RPAS certificate with a NAV CANADA authorisation filed before your date is confirmed.",
      },
      {
        question: "How does wind affect drone photography in Rundle?",
        answer:
          "More than any other variable. Rundle is flat and fully exposed to chinook winds, and it is the gust spread — not the average speed — that grounds an aircraft. Shoots are scheduled against the gust forecast, and a wind delay moves the date without restarting any paperwork.",
      },
      {
        question: "Can you fly near the Peter Lougheed Centre?",
        answer:
          "Yes, with planning. The hospital heliport operates about 1 km from Rundle, so every flight plan here is drawn to stay clear of its approach paths. That routing is set before the shoot is confirmed, alongside the standard CYYC authorisation, and it does not change what can be delivered.",
      },
    ],
    primaryKeywords: [
      "drone photography Rundle",
      "aerial photography Rundle",
      "Rundle drone services",
    ],
    nearestLocations: ["whitehorn", "marlborough", "temple"],
  },

  {
    slug: "saddle-ridge",
    name: "Saddle Ridge",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1225, lng: -113.9647 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 3,
      authorizationRequired: true,
      authorizationNote:
        "Saddle Ridge lies inside the charted CYYC control zone, about 3 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Flight plans here also keep clear of Peter Lougheed Centre heliport, operating about 5 km away. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Saddle Ridge is one of the few Calgary communities organised around a ring instead of a grid — Saddletowne Circle NE loops the retail and transit core, and the residential streets radiate out from it.",
      consequence:
        "From the ground that structure is invisible — a buyer cannot see how close a home actually sits to the CTrain, and a business inside the circle cannot show the community it serves.",
      resolution:
        "A rising reveal from the property up to circle height puts the Saddletowne core, the Genesis Centre of Community Wellness and the surrounding streets into a single explanatory frame, delivered in the standard turnaround.",
    },
    landmarks: [
      "Saddletowne CTrain Station",
      "Genesis Centre of Community Wellness",
      "Saddletowne Circle retail district",
      "Metis Trail NE",
      "Saddlecrest Boulevard NE",
    ],
    terrain:
      "Level new-community prairie built to a radial plan — the relief is architectural rather than geological, with density stepping down from the Saddletowne core to the outer crescents.",
    windNote:
      "Chinook belt with open exposure — level ground and wide arterials give a west wind nothing to slow it. Gust spread, not average speed, sets the flight window.",
    seasonalWindow:
      "Green-up transforms the young streetscape through late spring and summer, while winter chinooks cycle the snow cover on and off. Long summer evenings put low light down the radial streets.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and pathways, so Saddle Ridge launches are staged from private property with the client's consent.",
    subjectMix: ["new-build-subdivision", "luxury-residential", "commercial-retail", "construction-progress"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. Same-day work is realistic, and the location pairs naturally with Martindale, Taradale or Savanna on one mobilisation.",
    faqs: [
      {
        question: "Does Saddle Ridge's position near the airport rule out drone work?",
        answer:
          "No — it raises the bar for who can do it. At about 3 km from Calgary International, Saddle Ridge sits inside the CYYC Class C control zone, which requires an Advanced RPAS certificate and a NAV CANADA authorisation. Fly4MEdia carries both, files the authorisation at booking, and confirms the site on NAV Drone.",
      },
      {
        question: "What does aerial photography show in Saddle Ridge that ground photos miss?",
        answer:
          "The plan of the place. Saddle Ridge radiates out from Saddletowne Circle, so a home's real distance to the CTrain station, the Genesis Centre and the retail core only reads from altitude. One aerial frame answers the location questions a listing text struggles to.",
      },
      {
        question: "Can you shoot commercial properties around Saddletowne Circle?",
        answer:
          "Yes. The retail district around the circle photographs well from altitude because the ring layout makes catchment visible — the businesses at the centre, the community around them. Flights are planned within the authorised altitude for the site and keep clear of the charted approach paths.",
      },
    ],
    primaryKeywords: [
      "drone photography Saddle Ridge",
      "aerial photography Saddle Ridge",
      "Saddle Ridge drone services",
    ],
    nearestLocations: ["martindale", "taradale", "savanna", "redstone"],
  },

  {
    slug: "savanna",
    name: "Savanna",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1345, lng: -113.9508 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 5,
      authorizationRequired: true,
      authorizationNote:
        "Savanna sits about 5 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Savanna is a community being built inside controlled airspace — every framing crew and show home along Savanna Boulevard NE stands about 5 km from Calgary International, under the CYYC control zone as charted.",
      consequence:
        "For builders and marketers that geometry quietly narrows the field: most drone operators cannot legally take the job, and a progress series that needs the same flight repeated month after month demands an operator who can secure authorisation reliably, not once.",
      resolution:
        "Advanced certification makes it procedural — the NAV CANADA authorisation is filed with each booking, the site is confirmed on NAV Drone, and progress frames are flown to a consistent altitude and heading so the series actually compares, in the standard turnaround.",
    },
    landmarks: [
      "Savanna Central Park",
      "Savanna Boulevard NE",
      "88 Avenue NE",
      "Genesis Centre of Community Wellness",
    ],
    terrain:
      "Flat new-build prairie in mid-construction — finished crescents beside graded lots, so the aerial view changes month to month in a way few Calgary communities still do.",
    windNote:
      "Open chinook exposure at the city's northern edge. There is no shelter here; west gusts arrive unmoderated, and the gust spread sets the flying window.",
    seasonalWindow:
      "An active construction landscape reads differently through the year — spring mud, green-up on the finished streets in summer, winter snow that chinooks strip and return. Long summer evenings give the best residential light.",
    bylawNote:
      "City of Calgary bylaws restrict drone launches from municipal parks and pathways, Savanna Central Park included, so takeoffs are staged from private property with the client's consent.",
    subjectMix: ["new-build-subdivision", "construction-progress", "luxury-residential"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. A Savanna visit pairs efficiently with Saddle Ridge, Redstone or Martindale — useful for builders documenting more than one site in a single mobilisation.",
    faqs: [
      {
        question: "Can builders get drone photography inside Savanna's airspace?",
        answer:
          "Yes. Savanna sits about 5 km from Calgary International in the CYYC Class C control zone, so the work requires an Advanced RPAS certificate and a NAV CANADA authorisation. Fly4MEdia files the authorisation as part of each booking, which makes recurring progress flights a scheduling exercise rather than a legal one.",
      },
      {
        question: "How do repeat construction progress shoots work in Savanna?",
        answer:
          "Each pass is flown to the same altitude and heading so the frames align across months and the build's progress is legible at a glance. Authorisation is filed per booking, weather moves are simple calendar changes, and the finished set arrives in the standard turnaround after every flight.",
      },
    ],
    primaryKeywords: [
      "drone photography Savanna",
      "aerial photography Savanna",
      "Savanna drone services",
    ],
    nearestLocations: ["saddle-ridge", "martindale", "redstone", "taradale"],
  },

  {
    slug: "skyview-ranch",
    name: "Skyview Ranch",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1584, lng: -113.9536 },
    tier: 2,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 6,
      authorizationRequired: true,
      authorizationNote:
        "About 6 km from Calgary International, Skyview Ranch falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Skyview Ranch occupies the corner where Stoney Trail NE meets the Country Hills Boulevard NE corridor — from the air the community reads as a block framed by the two roads that carry its residents everywhere else.",
      consequence:
        "That frame is the sales argument — access — and it cannot be photographed from the ground. A listing claiming quick reach to the ring road shows a fence and a sound wall instead of the road relationship the buyer is paying for.",
      resolution:
        "An approach run flown along the corridor edge holds the property and the interchange geometry in one moving frame, so the access claim is visible rather than asserted — delivered in the standard turnaround.",
    },
    landmarks: [
      "Skyview Ranch Road NE",
      "Country Hills Boulevard NE corridor",
      "Stoney Trail NE",
      "Skyview Ranch Park",
      "Vivo for Healthier Generations",
    ],
    terrain:
      "Level northern prairie with young trees and dense rooflines — the strongest lines in an aerial frame here are man-made: arterials, sound walls and the ring road's sweep.",
    windNote:
      "Chinook belt, fully exposed. The flat approach from the west gives gusts a clean run at the community, so flyability is judged on gust spread rather than average wind.",
    seasonalWindow:
      "Young landscaping means green-up matters more here than in mature districts. Winter chinooks strip the snow between storms, and long summer evenings lay low light down the Country Hills Boulevard NE corridor.",
    bylawNote:
      "Drone takeoff and landing in City of Calgary parks, including Skyview Ranch Park, is restricted under parks bylaws, so launches are staged from private property with the client's consent.",
    subjectMix: ["new-build-subdivision", "construction-progress", "luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 25 minutes from the Calgary base via Stoney Trail NE — still an easy same-day booking, and one trip can also cover Redstone, Savanna or Homestead next door.",
    faqs: [
      {
        question: "Is Skyview Ranch inside the Calgary airport control zone?",
        answer:
          "Yes. The community sits about 6 km from Calgary International, inside the CYYC Class C control zone as charted, so drone flights require an Advanced RPAS certificate and a NAV CANADA authorisation. Fly4MEdia handles both as part of booking, and the specific site is confirmed against NAV Drone before every flight.",
      },
      {
        question: "Can an aerial shot show how close Skyview Ranch is to Stoney Trail?",
        answer:
          "Yes — that is the frame this community is built for. An approach run along the corridor holds a property and the ring-road interchange in one continuous shot, turning the access claim from a line in the listing into something a buyer can see for themselves.",
      },
      {
        question: "How far is Skyview Ranch for a same-day booking?",
        answer:
          "About 25 minutes from the Calgary base via Stoney Trail. That is comfortably inside same-day range when weather and authorisation align, and because neighbouring Redstone and Savanna are minutes away, a single mobilisation can cover several properties across the far northeast.",
      },
    ],
    primaryKeywords: [
      "drone photography Skyview Ranch",
      "aerial photography Skyview Ranch",
      "Skyview Ranch drone services",
    ],
    nearestLocations: ["redstone", "savanna", "homestead"],
  },

  {
    slug: "taradale",
    name: "Taradale",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1152, lng: -113.9808 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 2,
      authorizationRequired: true,
      authorizationNote:
        "Taradale lies inside the charted CYYC control zone, about 2 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Peter Lougheed Centre heliport operates about 4 km away, and flight plans keep clear of its approaches. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Taradale sits close enough to Calgary International that the airport's own weather station is effectively a local forecast — the wind CYYC reports from about 2 km away is the wind over Falconridge Boulevard NE within minutes.",
      consequence:
        "Chinook gust spreads that will ground an aircraft are therefore visible hours in advance — but only to an operator reading them. A shoot booked blind to that data risks a wasted appointment on a day the forecast plainly ruled out.",
      resolution:
        "Flight windows are set against the airport's own wind reporting, and because the NAV CANADA authorisation is filed at booking, a wind move is a calendar change rather than new paperwork — with delivery in the standard turnaround.",
    },
    landmarks: [
      "Taralake Way NE",
      "Falconridge Boulevard NE",
      "Genesis Centre of Community Wellness",
      "68 Street NE",
      "Village Square Leisure Centre",
    ],
    terrain:
      "Level northeast prairie laid out in crescents and cul-de-sacs, where street pattern and rooflines, not relief, give an aerial frame its structure.",
    windNote:
      "Open chinook exposure with no upwind shelter. West gusts cross the city and arrive at strength; the spread between mean and gust is what closes a flight window.",
    seasonalWindow:
      "Chinooks cycle the snow all winter, so white-ground frames need timing. Summer brings full canopy on the older streets and long, low evening light across the level grid.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing on municipal park land and pathways, so Taradale launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "new-build-subdivision", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. Same-day work is straightforward, and one mobilisation can also take in Saddle Ridge, Martindale or Castleridge.",
    faqs: [
      {
        question: "How close to Calgary International can you legally fly a drone?",
        answer:
          "Close — with the right authority. Taradale sits about 2 km from the airport inside the CYYC Class C control zone, and flights here require an Advanced RPAS certificate plus a NAV CANADA authorisation for the specific site and altitude. Fly4MEdia files that authorisation before your booking is confirmed.",
      },
      {
        question: "Why does wind cancel drone shoots in northeast Calgary?",
        answer:
          "Because gusts, not average speeds, ground an aircraft. Taradale's flat, open exposure lets chinook gusts arrive at full strength, and the spread can widen inside an hour. Fly4MEdia schedules against the airport's own wind reporting from about 2 km away, so cancellations are anticipated rather than discovered on site.",
      },
    ],
    primaryKeywords: [
      "drone photography Taradale",
      "aerial photography Taradale",
      "Taradale drone services",
    ],
    nearestLocations: ["saddle-ridge", "martindale", "castleridge"],
  },

  {
    slug: "temple",
    name: "Temple",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0917, lng: -113.9583 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 5,
      authorizationRequired: true,
      authorizationNote:
        "About 5 km from Calgary International, Temple falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Flight plans here also keep clear of Peter Lougheed Centre heliport, operating about 2 km away. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Temple's plan is curvilinear — Temple Drive NE bends through the community with courts and crescents branching off it, so almost no two lots sit square to the same street line.",
      consequence:
        "That geometry defeats ground photography. Lot orientation, yard depth and what a property actually backs onto are the questions Temple buyers ask, and none of them can be answered from the street.",
      resolution:
        "A top-down set over the property plus a slow orbit puts the lot lines, the orientation and the backing into plain view — flown under the site's authorisation and delivered in the standard turnaround.",
    },
    landmarks: [
      "Village Square Leisure Centre",
      "Temple Drive NE",
      "52 Street NE",
      "Temple Community Association",
    ],
    terrain:
      "Level northeast ground where street geometry does the work of relief — curving collectors and courts create the pattern an aerial frame organises itself around.",
    windNote:
      "Chinook belt on open, level ground. Nothing between Temple and the western horizon slows a gust, so flight windows are set by gust spread rather than average speed.",
    seasonalWindow:
      "Mature canopy fills summer frames and opens in winter to reveal the street geometry. Chinooks cycle the snow through the cold months; late-summer smoke is the main risk to contrast.",
    bylawNote:
      "Takeoff and landing in City of Calgary parks and on public pathways is restricted by bylaw, so Temple launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the Calgary base — an easy same-day booking that pairs naturally with Rundle, Pineridge or Monterey Park.",
    faqs: [
      {
        question: "What approvals does a drone shoot in Temple need?",
        answer:
          "Two things: an Advanced RPAS certificate and a NAV CANADA authorisation, because Temple sits about 5 km from Calgary International inside the CYYC Class C control zone. Fly4MEdia holds the certificate, files the authorisation as part of booking, and confirms the exact site against NAV Drone before flying.",
      },
      {
        question: "What does a buyer learn from aerial photos of a Temple property?",
        answer:
          "The shape of the lot and what it backs onto. Temple's curving streets mean lots sit at odd angles to one another, and orientation, yard depth and backing are invisible from the street. A top-down frame and a short orbit answer those questions in seconds.",
      },
      {
        question: "Does the Peter Lougheed Centre heliport limit flights over Temple?",
        answer:
          "It shapes routing rather than limiting the work. The heliport operates about 2 km away, so Temple flight plans are drawn to keep clear of its approaches. That routing is fixed before the shoot is confirmed and has no effect on what can be photographed over the property itself.",
      },
    ],
    primaryKeywords: [
      "drone photography Temple",
      "aerial photography Temple",
      "Temple drone services",
    ],
    nearestLocations: ["rundle", "pineridge", "monterey-park"],
  },

  {
    slug: "vista-heights",
    name: "Vista Heights",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0658, lng: -114.0139 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 6,
      authorizationRequired: true,
      authorizationNote:
        "Vista Heights sits about 6 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Peter Lougheed Centre heliport operates about 3 km away, and flight plans keep clear of its approaches. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Vista Heights faces the sunrise — its ground falls away eastward toward the Deerfoot Trail corridor, so first light hits this community's edge while the level grid behind it is still in shadow.",
      consequence:
        "The view is the community's name and its premium, and mid-day photography erases it — flat overhead light gives no depth cue, so a home on the edge looks identical to one in the middle of the grid.",
      resolution:
        "A golden-hour hold at the start of the day keeps shadow and relief in the frame, showing the drop and the outlook a Vista Heights address is priced on — delivered in the standard turnaround.",
    },
    landmarks: [
      "Vista Crescent NE",
      "Deerfoot Trail corridor",
      "19 Street NE",
      "Vista Heights Community Association",
    ],
    terrain:
      "A rise above the Deerfoot Trail corridor with the ground falling away on the community's eastern edge — genuine relief in a quarter of the city that is otherwise level.",
    windNote:
      "Chinook belt, with the community's elevated position openly exposed to west wind. As everywhere on this side of the city, the gust spread decides the flight window.",
    seasonalWindow:
      "The east-facing edge takes strong morning light year-round, and winter's low sun keeps texture in frames for more of the day. Chinooks cycle the snow; late-summer smoke can soften the long sightlines the community is named for.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and pathways, so Vista Heights launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Inside easy same-day range, and close enough to Renfrew, Mayland Heights and Bridgeland/Riverside to cover several inner-northeast shoots in one outing.",
    faqs: [
      {
        question: "Are drones permitted over Vista Heights?",
        answer:
          "Yes, under authorisation. Vista Heights lies about 6 km from Calgary International inside the CYYC Class C control zone, with the Peter Lougheed Centre heliport about 3 km away. Flights are conducted under an Advanced RPAS certificate with a NAV CANADA authorisation filed before the booking is confirmed.",
      },
      {
        question: "What time of day photographs Vista Heights best?",
        answer:
          "Early. The community's ground falls eastward toward the Deerfoot Trail corridor, so first light rakes across the edge and gives frames genuine depth. Mid-day sun flattens that relief entirely — which is why Vista Heights shoots are scheduled to the light window rather than to office hours.",
      },
    ],
    primaryKeywords: [
      "drone photography Vista Heights",
      "aerial photography Vista Heights",
      "Vista Heights drone services",
    ],
    nearestLocations: ["bridgeland-riverside", "renfrew", "mayland-heights"],
  },

  {
    slug: "whitehorn",
    name: "Whitehorn",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0817, lng: -113.9639 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 6,
      authorizationRequired: true,
      authorizationNote:
        "About 6 km from Calgary International, Whitehorn falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Peter Lougheed Centre heliport operates about 1 km away, and flight plans keep clear of its approaches. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "For much of the winter the sun over Whitehorn never climbs far, and mid-day light behaves the way golden hour does elsewhere — long shadows, warm colour, and texture across every roofline along Whitehaven Drive NE.",
      consequence:
        "Sellers assume winter aerials are a compromise and wait for spring — surrendering months of listing window even though the season's light is quietly doing them a favour.",
      resolution:
        "A winter golden-hour hold is easy to book — the window lands in mid-afternoon — and the flight is authorised as part of booking, with the finished set delivered in the standard turnaround.",
    },
    landmarks: [
      "Whitehorn LRT Station",
      "Whitehaven Drive NE",
      "52 Street NE",
      "Whitehorn Multi-Services Centre",
    ],
    terrain:
      "Level northeast grid whose aerial structure comes from transit, arterials and rooflines — the LRT line and its station give frames here an anchor most residential communities lack.",
    windNote:
      "Chinook country, flat and exposed. Winter gusts arrive with the warm wind, so the mild days that look ideal for flying are often the ones needing the closest watch on gust spread.",
    seasonalWindow:
      "Winter's low sun keeps texture in frames through the middle of the day, while summer compresses the best light into early and late windows. Chinooks strip the snow repeatedly, so fresh-snow frames are a matter of timing.",
    bylawNote:
      "Drone takeoff and landing in City of Calgary parks and on public pathways is restricted by parks bylaws, so Whitehorn launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the Calgary base — same-day range, with Marlborough, Rundle and Mayland Heights close enough to share a single mobilisation.",
    faqs: [
      {
        question: "Does the Peter Lougheed Centre heliport affect drone flights in Whitehorn?",
        answer:
          "It affects routing, not availability. The heliport operates about 1 km away, so Whitehorn flight plans are drawn to stay clear of its approach paths before the shoot is confirmed. The larger requirement is the CYYC control zone itself, which Fly4MEdia flies under Advanced certification with NAV CANADA authorisation.",
      },
      {
        question: "Is winter a bad time for aerial photography in Whitehorn?",
        answer:
          "No — it is quietly the best light of the year. The winter sun stays low enough that even mid-day frames carry long shadows and warm tone, and the golden-hour window lands at a bookable mid-afternoon hour. Chinook gusts are the real constraint, and those are forecast, not guessed.",
      },
    ],
    primaryKeywords: [
      "drone photography Whitehorn",
      "aerial photography Whitehorn",
      "Whitehorn drone services",
    ],
    nearestLocations: ["marlborough", "rundle", "mayland-heights"],
  },

  {
    slug: "winston-heights-mountview",
    name: "Winston Heights/Mountview",
    region: "calgary-ne",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0644, lng: -114.0347 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 7,
      authorizationRequired: true,
      authorizationNote:
        "Winston Heights/Mountview lies inside the charted CYYC control zone, about 7 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Peter Lougheed Centre heliport operates about 4 km away, and flight plans keep clear of its approaches. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Peter Lougheed Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "From the air, Winston Heights/Mountview is a street network interrupted by fairways — the Winston Golf Club occupies a broad share of the community, and roads like Mount View Road NE end against the course rather than connecting through.",
      consequence:
        "Golf-course adjacency is the value driver here, and it is unprovable from the street — a fence and a tree line look the same whether they hide a fairway or a utility corridor.",
      resolution:
        "A boundary-line orbit along the course edge shows exactly where a property stands relative to the fairways — the adjacency argued in pictures rather than words, and delivered in the standard turnaround.",
    },
    landmarks: [
      "Winston Golf Club",
      "Confederation Park",
      "Mount View Road NE",
      "James Fowler High School",
    ],
    terrain:
      "An established inner-northeast community whose largest open spaces are turf — golf fairways inside the boundary and Confederation Park at its flank, set against a compact street grid.",
    windNote:
      "Chinook belt like the rest of the city — west winds arrive with little to slow them, and the gust spread governs the flight window on any given day.",
    seasonalWindow:
      "The fairways drive the calendar: deep green through summer, frost and snow textures in the cold months when chinooks permit. Evening light through the mature canopy is the community's best hour.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks, Confederation Park included, so launches are staged from private property with the client's consent — golf club land needs the club's own permission.",
    subjectMix: ["luxury-residential", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "About 15 minutes from the Calgary base — comfortably same-day, and adjacent to Renfrew, Highland Park and Bridgeland/Riverside for multi-property outings.",
    faqs: [
      {
        question: "What airspace rules apply to Winston Heights/Mountview?",
        answer:
          "The community sits about 7 km from Calgary International, inside the CYYC Class C control zone, with the Peter Lougheed Centre heliport about 4 km away. Drone flights require an Advanced RPAS certificate and a NAV CANADA authorisation, both of which Fly4MEdia handles before confirming your shoot date.",
      },
      {
        question: "Can you photograph a home backing onto the Winston Golf Club?",
        answer:
          "Yes — that adjacency is the frame most worth buying. An orbit flown along the property's boundary shows the home and the fairway in one shot, proving the backing a street photo cannot. Launches are staged from private property with consent, and the flight stays within the authorised altitude.",
      },
    ],
    primaryKeywords: [
      "drone photography Winston Heights/Mountview",
      "aerial photography Winston Heights/Mountview",
      "Winston Heights/Mountview drone services",
    ],
    nearestLocations: ["renfrew", "bridgeland-riverside", "highland-park", "cambrian-heights"],
  }
];
